/**
 * UserAvatar.tsx
 *
 * Avatar de usuario con color dinámico de la paleta del sistema.
 * Muestra imagen de perfil si existe, iniciales con color elegido si no.
 *
 * Uso:
 *   <UserAvatar name="Guillermo Sandoval" color="oklch(0.68 0.21 25)" />
 *   <UserAvatar name="Guillermo Sandoval" color="oklch(0.68 0.21 25)" src="/foto.jpg" size="lg" />
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { cn } from '@/core/lib/utils';
import { getTextColorFromToken, getColorValue } from '@/core/lib/colors';
import { getInitials } from '@/core/lib/get-initials';

/* ── Variantes ──────────────────────────────────────────────── */

const avatarVariants = cva('rounded-full', {
  variants: {
    size: {
      xs: 'size-5', // 20px — en listas densas, stacks de avatares
      sm: 'size-6', // 24px — inline en filas de tareas
      md: 'size-8', // 32px — default
      lg: 'size-10', // 40px — perfil, cards
      xl: 'size-12', // 48px — página de perfil
      '2xl': 'size-[60px] md:size-[100px]', // settings — mobile/desktop
    },
  },
  defaultVariants: { size: 'md' },
});

const fallbackVariants = cva('flex items-center justify-center font-medium select-none', {
  variants: {
    size: {
      xs: 'text-[9px]',
      sm: 'text-[10px]',
      md: 'text-xs',
      lg: 'text-sm',
      xl: 'text-base',
      '2xl': 'text-lg md:text-2xl',
    },
  },
  defaultVariants: { size: 'md' },
});

/* ── Props ──────────────────────────────────────────────────── */

export interface UserAvatarProps extends VariantProps<typeof avatarVariants> {
  /** Nombre completo del usuario — usado para generar iniciales y color por defecto */
  name: string;
  /** Token del color de paleta (ej: "red-ember") — se resuelve internamente a oklch. Si no se provee, se genera uno automático */
  color: string;
  /** URL de imagen de perfil — si existe, se muestra en lugar de las iniciales */
  src?: string;
  /** Alt text para la imagen */
  alt?: string;
  className?: string;
}

/* ── Componente ─────────────────────────────────────────────── */

export function UserAvatar({ name, color, src, alt, size, className }: UserAvatarProps) {
  const initials = getInitials(name);
  const colorValue = getColorValue(color);
  const textColor = getTextColorFromToken(color);

  return (
    <Avatar className={cn(avatarVariants({ size }), className)}>
      <AvatarImage src={src} alt={alt ?? name} />
      <AvatarFallback
        className={cn(fallbackVariants({ size }))}
        style={{ background: colorValue, color: textColor }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
